'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { LayoutGrid, Users, TrendingUp, Target } from 'lucide-react';

interface Plan {
    name: string;
}

interface WhatIsPlanSectionProps {
    plan: Plan;
}

const featureConfig = [
    {
        icon: LayoutGrid,
        cardClass: 'from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600',
        iconClass: 'bg-primary-500 dark:bg-primary-600',
        titleKey: 'features.limitedWidth.title',
        subtitleKey: 'features.limitedWidth.subtitle'
    },
    {
        icon: Users,
        cardClass: 'from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600',
        iconClass: 'bg-blue-500 dark:bg-blue-600',
        titleKey: 'features.predeterminedLevels.title',
        subtitleKey: 'features.predeterminedLevels.subtitle'
    },
    {
        icon: TrendingUp,
        cardClass: 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600',
        iconClass: 'bg-green-500 dark:bg-green-600',
        titleKey: 'features.noBreakaway.title',
        subtitleKey: 'features.noBreakaway.subtitle'
    },
    {
        icon: Target,
        cardClass: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600',
        iconClass: 'bg-purple-500 dark:bg-purple-600',
        titleKey: 'features.multipleSponsors.title',
        subtitleKey: 'features.multipleSponsors.subtitle'
    },
];

export function WhatIsPlanSection({ plan }: WhatIsPlanSectionProps) {
    const t = useTranslations('planDetails.forcedMatrixPlan.whatIsPlan');

    return (
        <SectionWrapper variant="default" padding="md" className="relative bg-white dark:bg-gray-950">
            <div className="container">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-8">
                        <div>
                            <Typography variant="h2" className="mb-6 text-gray-900 dark:text-white">
                                {t('title')}
                            </Typography>
                        </div>
                        <div className="relative">
                            <Typography variant="p" className="">
                                {t('description')}
                            </Typography>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        {featureConfig.map(({ icon: Icon, cardClass, iconClass, titleKey, subtitleKey }, index) => (
                            <div
                                key={index}
                                className={`bg-gradient-to-br ${cardClass} rounded-2xl p-6 border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col items-center text-center`}
                            >
                                <div className={`w-14 h-14 rounded-xl ${iconClass} flex items-center justify-center mb-4`}>
                                    <Icon className="w-7 h-7 text-white" />
                                </div>
                                <Typography variant="h4" className="text-md font-bold text-gray-900 dark:text-white mb-2">
                                    {t(titleKey)}
                                </Typography>
                                <Typography variant="p" className="text-sm text-gray-600 dark:text-gray-400">
                                    {t(subtitleKey)}
                                </Typography>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

